/**
 * MobileGridStrategy
 *
 * Mobile landscape strategy with 2x2 grid layout.
 * Shows all 4 primary filters in a compact grid optimized for landscape orientation.
 *
 * SOLID Compliance:
 * - SRP: Only renders mobile landscape layout
 * - OCP: Extends FilterUIStrategy (can be replaced without modification)
 * - LSP: Fully substitutable for FilterUIStrategy
 * - ISP: Implements only required strategy interface
 * - DIP: Depends on abstractions (URLStateManager, FilterRepository)
 *
 * Layout (2x2 Grid):
 * ┌─────────────────┬─────────────────┐
 * │   Location      │    Date         │
 * ├─────────────────┼─────────────────┤
 * │   Guests        │  Activity Type  │
 * └─────────────────┴─────────────────┘
 *        [Search Button]
 *
 * Usage:
 * const strategy = new MobileGridStrategy();
 * const ui = strategy.render(filters, urlStateManager, filterRepository);
 *
 * Location: apps/web/src/components/search/strategies/MobileGridStrategy.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System - Phase 3
 */

import React, { useState, useRef } from 'react';
import { Search } from 'lucide-react';
import FilterUIStrategy from './FilterUIStrategy';
import LocationAutocomplete from '../LocationAutocomplete';
import SearchDateRangePicker from '../../calendar/SearchDateRangePicker';
import GuestCountSelector from '../../calendar/GuestCountSelector';
import ActivityTypeSelector from '../ActivityTypeSelector';
import useSearchFilters from '../../../hooks/useSearchFilters';

class MobileGridStrategy extends FilterUIStrategy {
  getType() {
    return 'mobile-landscape';
  }

  supportsAdvancedFilters() {
    return false; // Mobile landscape doesn't show advanced filters
  }

  getVisiblePrimaryFilterCount() {
    return 4; // All 4 primary filters visible in grid
  }

  render(filters, urlStateManager, filterRepository) {
    return (
      <MobileGridStrategyComponent
        filters={filters}
        urlStateManager={urlStateManager}
        filterRepository={filterRepository}
      />
    );
  }
}

/**
 * MobileGridStrategyComponent - React Component
 */
function MobileGridStrategyComponent({ filters, urlStateManager, filterRepository: _filterRepository }) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const datePickerRef = useRef(null);
  const guestPickerRef = useRef(null);
  const activityPickerRef = useRef(null);

  // Use centralized search filters hook
  const {
    locationInput: location,
    localStartDate: startDate,
    localEndDate: endDate,
    localGuestCount: guestCount,
    localActivities,
    handleLocationChange,
    handleLocationSelect,
    handleDateRangeSelect: hookHandleDateRangeSelect,
    handleGuestCountChange: hookHandleGuestCountChange,
    handleActivityChange: hookHandleActivityChange,
    getDateDisplay,
    getGuestDisplay,
    formatDateForAPI,
  } = useSearchFilters(filters, urlStateManager);

  // Extract activity display values
  const activitySlug = localActivities.length > 0 ? localActivities[0].slug : '';
  const activityName = localActivities.length > 0 ? localActivities[0].name : '';

  /**
   * Wrapper for date range - closes picker after selection
   * Note: MobileGrid updates URL immediately via handleLocationSelect from hook
   */
  const handleDateRangeSelect = (start, end) => {
    hookHandleDateRangeSelect(start, end);
    // Immediately sync to URL for mobile (different from desktop pattern)
    urlStateManager.updateFilters({
      startDate: formatDateForAPI(start),
      endDate: formatDateForAPI(end)
    });
    setShowDatePicker(false);
  };

  /**
   * Wrapper for guest count - closes picker and syncs to URL immediately
   */
  const handleGuestCountChange = (count) => {
    hookHandleGuestCountChange(count);
    // Immediately sync to URL for mobile
    urlStateManager.updateFilter('partySize', count);
    setShowGuestPicker(false);
  };

  /**
   * Wrapper for activity type - closes picker and syncs to URL immediately
   * Note: Hook expects array, but mobile uses single selection
   */
  const handleActivityChange = (slug, name) => {
    hookHandleActivityChange([{ slug, name }]);
    // Immediately sync to URL for mobile
    urlStateManager.updateFilters({
      activitySlug: slug,
      activityName: name
    });
    setShowActivityPicker(false);
  };

  /**
   * Execute search - filters already in URL from immediate sync
   * Search button is just for user confirmation/completion
   */
  const handleSearch = () => {
    // No-op: filters already synced to URL via immediate updates
    // Search results update automatically via useEffect in App.jsx
  };

  /**
   * Get display text for activity type
   */
  const getActivityDisplay = () => {
    return activityName || activitySlug || 'Any type';
  };

  return (
    <div className="w-full px-4">
      {/* 2x2 Grid Layout */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Location - Top Left */}
        <div className="border border-gray-300 rounded-lg p-3 bg-white">
          <div className="text-xs font-semibold text-gray-900 mb-2">LOCATION</div>
          <LocationAutocomplete
            value={location}
            onChange={handleLocationChange}
            onSelect={handleLocationSelect}
            placeholder="Anywhere"
            country="US"
            debugId="MobileGridStrategy"
          />
        </div>

        {/* Date - Top Right */}
        <div className="relative border border-gray-300 rounded-lg p-3 bg-white" ref={datePickerRef}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full text-left"
          >
            <div className="text-xs font-semibold text-gray-900 mb-2">DATE</div>
            <div className={`text-sm ${startDate && endDate ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
              {getDateDisplay()}
            </div>
          </button>
          {showDatePicker && (
            <div className="absolute top-full left-0 mt-2 z-50">
              <SearchDateRangePicker
                onDateRangeSelect={handleDateRangeSelect}
                onClose={() => setShowDatePicker(false)}
              />
            </div>
          )}
        </div>

        {/* Guests - Bottom Left */}
        <div className="relative border border-gray-300 rounded-lg p-3 bg-white" ref={guestPickerRef}>
          <button
            onClick={() => setShowGuestPicker(!showGuestPicker)}
            className="w-full text-left"
          >
            <div className="text-xs font-semibold text-gray-900 mb-2">GUESTS</div>
            <div className={`text-sm ${guestCount > 1 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
              {getGuestDisplay()}
            </div>
          </button>
          {showGuestPicker && (
            <div className="absolute top-full left-0 mt-2 z-50">
              <GuestCountSelector
                value={guestCount}
                onChange={handleGuestCountChange}
                onClose={() => setShowGuestPicker(false)}
              />
            </div>
          )}
        </div>

        {/* Activity Type - Bottom Right */}
        <div className="relative border border-gray-300 rounded-lg p-3 bg-white" ref={activityPickerRef}>
          <button
            onClick={() => setShowActivityPicker(!showActivityPicker)}
            className="w-full text-left"
          >
            <div className="text-xs font-semibold text-gray-900 mb-2">ADVENTURE TYPE</div>
            <div className={`text-sm ${activityName || activitySlug ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
              {getActivityDisplay()}
            </div>
          </button>
          {showActivityPicker && (
            <div className="absolute top-full left-0 right-0 mt-2 z-50 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 max-h-80 overflow-y-auto">
              <ActivityTypeSelector
                value={{ slug: activitySlug, name: activityName }}
                onChange={handleActivityChange}
                variant="inline"
              />
            </div>
          )}
        </div>
      </div>

      {/* Search Button */}
      <button
        onClick={handleSearch}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition shadow-md flex items-center justify-center gap-2"
      >
        <Search className="w-5 h-5" />
        Search
      </button>
    </div>
  );
}

export default MobileGridStrategy;
